import React, { useState } from 'react';
import {
  Box, Grid, Typography, Paper, TextField, Button, Skeleton, Snackbar, Alert,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';

import { PageHeader, MetricCard, SkeletonCards, ErrorState, PermissionState, useDesignTokens } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';
import { radius } from '../../themes/smartinventoryTheme';
import useGAReportingPage from './useGAReportingPage';

const pctDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}%` };
};

const countDelta = (value) => {
  if (value == null) return undefined;
  return { direction: value > 0 ? 'up' : value < 0 ? 'down' : 'neutral', text: `${value > 0 ? '+' : ''}${value}` };
};

const GAReporting = () => {
  const p = useGAReportingPage();
  const { tokens: t } = useDesignTokens();
  const [csvSnackbar, setCsvSnackbar] = useState(false);

  if (p.effectiveStatus === 'loading') {
    return (
      <Box>
        <SkeletonCards rows={1} />
        <Grid container spacing={2} sx={{ mb: 2, mt: 1 }}>
          {[0, 1, 2, 3].map((i) => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={100} sx={{ borderRadius: `${radius.lg}px` }} /></Grid>)}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={260} sx={{ borderRadius: `${radius.xl}px` }} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={260} sx={{ borderRadius: `${radius.xl}px` }} /></Grid>
        </Grid>
      </Box>
    );
  }

  if (p.effectiveStatus === 'error') {
    return (
      <ErrorState
        title={p.t("Couldn't load platform analytics", { defaultValue: "Couldn't load platform analytics" })}
        description={p.t('The server did not respond. Your data is safe — nothing was changed.', { defaultValue: 'The server did not respond. Your data is safe — nothing was changed.' })}
        request={p.errorInfo?.request}
        retryLabel={p.t('Try again', { defaultValue: 'Try again' })}
        onRetry={p.fetchMetrics}
      />
    );
  }

  if (p.effectiveStatus === 'permission') {
    return (
      <PermissionState
        title={p.t("You don't have access to Platform analytics", { defaultValue: "You don't have access to Platform analytics" })}
        description={p.t('Ask a platform operator for access.', { defaultValue: 'Ask a platform operator for access.' })}
      />
    );
  }

  const d = p.data;

  return (
    <Box>
      <PageHeader
        title={p.t('Platform analytics', { defaultValue: 'Platform analytics' })}
        subtitle={p.t('Usage and revenue across all companies', { defaultValue: 'Usage and revenue across all companies' })}
        actions={[
          { label: p.t('Export CSV', { defaultValue: 'Export CSV' }), icon: DownloadIcon, variant: 'outlined', onClick: () => setCsvSnackbar(true) },
        ]}
      />

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" type="date" label={p.t('From', { defaultValue: 'From' })} value={p.fromInput} onChange={(e) => p.setFromInput(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label={p.t('To', { defaultValue: 'To' })} value={p.toInput} onChange={(e) => p.setToInput(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={p.runReport}>{p.t('Run report', { defaultValue: 'Run report' })}</Button>
      </Paper>

      {d.insight && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5, bgcolor: 'primary.light', borderColor: 'primary.main' }}>
          <TrendingUpIcon sx={{ color: 'primary.main' }} />
          <Typography variant="body2" fontWeight={600}>{d.insight}</Typography>
        </Paper>
      )}

      <Grid container spacing={1.75} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('GMV', { defaultValue: 'GMV' })} value={formatCurrency(d.kpis.gmv.value)} delta={pctDelta(d.kpis.gmv.delta)} sparkline={d.kpis.gmv.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Orders')} value={d.kpis.orders.value} delta={pctDelta(d.kpis.orders.delta)} sparkline={d.kpis.orders.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('Active companies', { defaultValue: 'Active companies' })} value={d.kpis.activeCompanies.value} delta={countDelta(d.kpis.activeCompanies.delta)} sparkline={d.kpis.activeCompanies.sparkline} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard label={p.t('New signups', { defaultValue: 'New signups' })} value={d.kpis.newSignups.value} delta={countDelta(d.kpis.newSignups.delta)} sparkline={d.kpis.newSignups.sparkline} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography variant="subtitle1">{p.t('Orders across the platform · daily', { defaultValue: 'Orders across the platform · daily' })}</Typography>
              <Typography variant="body2" fontWeight={700}>{d.kpis.orders.value}</Typography>
            </Box>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={d.ordersDaily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gaOrdersFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={t.accent} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={t.accent} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke={t.border} />
                <XAxis dataKey="date" tick={{ fontSize: 10.5, fill: t.muted }} tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} axisLine={{ stroke: t.border }} tickLine={false} minTickGap={40} />
                <YAxis tick={{ fontSize: 10.5, fill: t.muted }} axisLine={false} tickLine={false} width={36} />
                <RechartsTooltip labelFormatter={(v) => new Date(v).toLocaleDateString()} contentStyle={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: radius.md, fontSize: 12.5 }} />
                <Area type="monotone" dataKey="value" stroke={t.accent} strokeWidth={2} fill="url(#gaOrdersFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper variant="outlined" sx={{ p: 2.5, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>{p.t('GMV by company', { defaultValue: 'GMV by company' })}</Typography>
            {d.gmvByCompany.length === 0 ? (
              <Typography variant="body2" color="text.secondary">{p.t('No revenue in this period.', { defaultValue: 'No revenue in this period.' })}</Typography>
            ) : d.gmvByCompany.map((c) => (
              <Box key={c.companyId} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.25 }}>
                <Typography variant="body2" color="text.secondary" sx={{ width: 100, flexShrink: 0 }} noWrap>{c.name}</Typography>
                <Box sx={{ flex: 1, height: 14, borderRadius: `${radius.sm}px`, bgcolor: 'action.selected', overflow: 'hidden' }}>
                  <Box sx={{ width: `${c.share}%`, height: '100%', bgcolor: 'primary.main' }} />
                </Box>
                <Typography variant="body2" fontWeight={700} sx={{ width: 64, textAlign: 'right', flexShrink: 0 }}>{formatCurrency(c.value)}</Typography>
              </Box>
            ))}
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider', mt: 1.25, pt: 1.25 }}>
              <Typography variant="caption" color="text.secondary">{p.t('Total GMV', { defaultValue: 'Total GMV' })}</Typography>
              <Typography variant="h6">{formatCurrency(d.kpis.gmv.value)}</Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={csvSnackbar} autoHideDuration={4000} onClose={() => setCsvSnackbar(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="info" sx={{ width: '100%' }}>{p.t("CSV export isn't available for this report yet.", { defaultValue: "CSV export isn't available for this report yet." })}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GAReporting;
